import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useEntity } from "../../contexts/EntityContext";
import { usePortfolioConfig } from "../../contexts/PortfolioConfigContext";
import {
  getLatestPortfolio,
  getPortfolioHistory,
  buildNetWorthSeries,
  computeReturnYTD,
  computeReturnMTD,
  buildProductAllocation,
  buildCurrencyBreakdown,
  buildPositions,
  getPortfolioByDate,
  getPortfolioHistoryForYear,
  getPortfolioYearRange,
} from "../../services/portfolioService";
import "./PortfolioPage.css";
import Header from "../../components/Header";
import PortfolioConfigModal from "../../components/PortfolioConfigModal";
import {
  getConsolidatedLatest,
  getConsolidatedByDate,
  getConsolidatedHistory,
} from "../../services/portfolioConsolidatedService";

function formatCurrency(amount, currency = "CLP") {
  try {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency,
    }).format(amount || 0);
  } catch {
    return `${currency} ${Number(amount || 0).toLocaleString("es-CL")}`;
  }
}

function percentText(value, decimals = 2) {
  return `${Number(value || 0).toFixed(decimals)}%`;
}

function formatMillions(amount, decimals = 1) {
  const millions = Number(amount || 0) / 1_000_000;
  return `$${millions.toFixed(decimals)}M`;
}

// Helper: case-insensitive property getter
function getPropCI(obj, wantedKey) {
  if (!obj || typeof obj !== "object") return undefined;
  const lower = String(wantedKey).toLowerCase();
  for (const key of Object.keys(obj)) {
    if (String(key).toLowerCase() === lower) return obj[key];
  }
  return undefined;
}

// Helper: get nested path with case-insensitive keys; supports arrays (picking first element)
function getPathCI(root, path) {
  let current = root;
  for (const step of path) {
    if (Array.isArray(current)) {
      current = current[0];
    }
    current = getPropCI(current, step);
    if (current == null) return undefined;
  }
  return current;
}

// Extract cash (Amount) from allocationAggregated → Portfolios[] → Allocation[] (Tipo de Producto) → Items where Name === "Efectivo"
function extractCashFromAllocationsCI(root) {
  if (!root) return 0;
  const pickFromAgg = (agg) => {
    if (!agg) return 0;
    const portfolios = getPropCI(agg, "Portfolios");
    if (!Array.isArray(portfolios)) return 0;
    for (const pf of portfolios) {
      const sections =
        getPropCI(pf, "Allocation") || getPropCI(pf, "Allocations");
      if (!Array.isArray(sections)) continue;
      // Prefer section whose Description menciona "Producto"
      let candidates = sections;
      const prodSection = sections.find((s) => {
        const desc = getPropCI(s, "Description");
        return String(desc || "")
          .toLowerCase()
          .includes("producto");
      });
      if (prodSection) candidates = [prodSection];
      for (const sec of candidates) {
        const items = getPropCI(sec, "Items");
        if (!Array.isArray(items)) continue;
        const efectivo = items.find(
          (it) =>
            String(getPropCI(it, "Name") ?? "").toLowerCase() === "efectivo"
        );
        if (efectivo) {
          // Prefer Amount per latest schema; fallback to CurrencyAmount if present
          const amt = getPropCI(efectivo, "Amount");
          const ca = getPropCI(efectivo, "CurrencyAmount");
          const val = Number((amt ?? ca) || 0);
          if (Number.isFinite(val)) return val;
        }
      }
    }
    return 0;
  };
  // Try portfolio.allocationAggregated first
  const portfolio = getPropCI(root, "portfolio") || root;
  const agg1 =
    getPropCI(portfolio, "allocationAggregated") ||
    getPropCI(portfolio, "AllocationAggregated");
  const v1 = pickFromAgg(agg1);
  if (v1) return v1;
  // Fallback: validation.data.allocationAggregated
  const agg2 =
    getPathCI(root, ["validation", "data", "allocationAggregated"]) ||
    getPathCI(root, ["validation", "data", "AllocationAggregated"]);
  const v2 = pickFromAgg(agg2);
  return Number.isFinite(v2) ? v2 : 0;
}

function LineChart({
  data,
  height = 260,
  currency = "CLP",
  selectedDate = null,
  onDateSelect = () => {},
}) {
  const [hover, setHover] = React.useState(null); // { i, x, y }
  const containerRef = React.useRef(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  React.useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cw = entry.contentRect?.width || 0;
        if (cw && cw !== containerWidth) setContainerWidth(cw);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [containerWidth]);
  if (!data || data.length < 2)
    return <div className="chart-empty">Sin datos suficientes</div>;
  const width = Math.max(320, containerWidth || 640);
  const padding = 28;
  const ys = data.map((d) => d.value);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const n = data.length;
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;
  const scaleX = (i) => padding + (i * innerW) / (n - 1);
  const scaleY = (y) =>
    padding + innerH * (1 - (y - minY) / Math.max(1, maxY - minY));
  const points = data.map((d, i) => [scaleX(i), scaleY(d.value)]);
  // Axis ticks
  const yTickCount = 5;
  const yTicks = Array.from({ length: yTickCount }, (_, k) => {
    const t = k / (yTickCount - 1);
    const v = minY + (maxY - minY) * t;
    return { y: scaleY(v), v };
  });
  // Month ticks at the first occurrence of each month to align with actual data points
  const monthTicks = (() => {
    const ticks = [];
    let lastYm = "";
    for (let i = 0; i < n; i++) {
      const ym = (data[i].date || "").slice(0, 7);
      if (ym && ym !== lastYm) {
        ticks.push({ x: scaleX(i), ym });
        lastYm = ym;
      }
    }
    return ticks;
  })();
  const fmtAxis = (v) => {
    const millions = v / 1_000_000;
    const abs = Math.abs(millions);
    const prec = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    return new Intl.NumberFormat("es-CL", {
      minimumFractionDigits: prec,
      maximumFractionDigits: prec,
    }).format(millions);
  };
  // const fmtDateShort = (iso) => {
  //   const [y, m, d] = iso.split('-').map(Number);
  //   const dateUtc = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  //   return new Intl.DateTimeFormat('es-CL', { month: 'short', day: '2-digit', timeZone: 'UTC' }).format(dateUtc);
  // };
  const fmtMonth = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(Date.UTC(y, (m || 1) - 1, 1));
    // Force UTC to avoid off-by-one month due to local TZ
    return new Intl.DateTimeFormat("es-CL", {
      month: "short",
      timeZone: "UTC",
    }).format(d);
  };
  const path = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x} ${y}`)
    .join(" ");

  // removed: no longer used
  const formatDate = (iso) => {
    // data[i].date es YYYY-MM-DD
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y.slice(2)}`;
  };

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left; // svg local x
    const t = Math.max(0, Math.min(1, (mx - padding) / innerW));
    const i = Math.round(t * (n - 1));
    const [x, y] = points[i];
    setHover({ i, x, y });
  };
  const onLeave = () => setHover(null);
  const onClick = (e) => {
    let index = hover?.i;
    if (typeof index !== "number") {
      const rect = e.currentTarget.getBoundingClientRect();
      const mx = e.clientX - rect.left; // svg local x
      const t = Math.max(0, Math.min(1, (mx - padding) / innerW));
      index = Math.round(t * (n - 1));
    }
    const picked = data[index];
    if (picked && picked.date) onDateSelect(picked.date);
  };

  return (
    <div
      ref={containerRef}
      className="linechart-container"
      style={{ position: "relative", width: "100%" }}
    >
      <svg
        className="line-chart"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onClick={onClick}
      >
        {/* axes */}
        <line
          x1={padding}
          y1={height - padding}
          x2={width - padding}
          y2={height - padding}
          className="chart-axis"
        />
        <line
          x1={padding}
          y1={padding}
          x2={padding}
          y2={height - padding}
          className="chart-axis"
        />
        {/* y grid + labels */}
        {yTicks.map((t, idx) => (
          <g key={`y-${idx}`}>
            <line
              x1={padding}
              y1={t.y}
              x2={width - padding}
              y2={t.y}
              className="chart-grid"
            />
            <text
              x={padding - 8}
              y={t.y + 4}
              textAnchor="end"
              className="chart-label"
              style={{ fontSize: 10 }}
            >
              {fmtAxis(t.v)}
            </text>
          </g>
        ))}
        {/* month end markers + labels */}
        {monthTicks.map((t, idx) => (
          <g key={`mx-${idx}`}>
            <line
              x1={t.x}
              y1={padding}
              x2={t.x}
              y2={height - padding}
              className="chart-grid-strong"
            />
            <text
              x={t.x}
              y={height - padding + 16}
              textAnchor="middle"
              className="chart-label"
              style={{ fontSize: 10 }}
            >
              {fmtMonth(t.ym)}
            </text>
          </g>
        ))}
        {/* line with contrast halo */}
        <path d={path} className="main-line-halo" fill="none" />
        <path d={path} className="main-line" fill="none" />
        {hover && (
          <>
            <circle
              cx={points[hover.i][0]}
              cy={points[hover.i][1]}
              r="4"
              fill="#10b981"
              stroke="#fff"
              strokeWidth="2"
            />
            <line
              x1={points[hover.i][0]}
              y1={padding}
              x2={points[hover.i][0]}
              y2={height - padding}
              stroke="rgba(16,185,129,.25)"
            />
          </>
        )}
        {selectedDate &&
          (() => {
            const selIndex = data.findIndex((d) => d.date === selectedDate);
            if (selIndex >= 0) {
              const [sx, sy] = points[selIndex];
              return (
                <>
                  <circle
                    cx={sx}
                    cy={sy}
                    r="5"
                    fill="var(--accent-color, #0ea5e9)"
                    stroke="#fff"
                    strokeWidth="2"
                  />
                  <line
                    x1={sx}
                    y1={padding}
                    x2={sx}
                    y2={height - padding}
                    stroke="rgba(14,165,233,.35)"
                  />
                </>
              );
            }
            return null;
          })()}
      </svg>
      {hover && (
        <div
          className="chart-tooltip"
          style={{
            position: "absolute",
            left: Math.min(Math.max(hover.x + 8, 8), width - 160),
            top: Math.max(hover.y - 36, 8),
          }}
        >
          <div className="tt-row">
            {new Intl.NumberFormat("es-CL", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }).format(data[hover.i].value / 1_000_000)}{" "}
            - {formatDate(data[hover.i].date)}
          </div>
        </div>
      )}
    </div>
  );
}

function MonthlyStackedChart({
  monthly,
  height = 240,
  selectedMonth = null,
  scaleMax: externalScaleMax = null,
  legendReservedHeight = 0,
}) {
  const [hover, setHover] = React.useState(null); // { type, value, x, y, month }
  const fmt = (n) =>
    new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  if (!monthly || monthly.length === 0)
    return <div className="chart-empty">Sin datos mensuales</div>;
  const padding = 32;
  const barW = 28;
  const gap = 18;
  const width = padding * 2 + monthly.length * (barW + gap) - gap;
  const innerH = height - padding * 2;
  const halfH = innerH / 2;
  const posMax = Math.max(
    ...monthly.map((m) => {
      const aportes = Math.max(0, Number(m.aportes || 0));
      const retornoVal = Number(m.retorno || 0);
      const retornoPos = Math.max(0, retornoVal);
      return aportes + retornoPos;
    })
  );
  const negMax = Math.max(
    ...monthly.map((m) => {
      const rescates = Math.max(0, Number(m.rescates || 0));
      const retornoVal = Number(m.retorno || 0);
      const retornoNeg = Math.max(0, -retornoVal);
      return rescates + retornoNeg;
    })
  );
  const maxMag =
    Math.max(1, externalScaleMax || 0, externalScaleMax == null ? 0 : 0) ||
    Math.max(1, posMax, negMax);
  const yZero = padding + halfH;
  const scale = (v) => (Math.abs(Number(v) || 0) / maxMag) * halfH;
  const toLabel = (ym) => {
    // Render month label in UTC to avoid TZ shifts (e.g., CLT showing previous month)
    const [yy, mm] = ym.split("-").map(Number);
    const dateUtc = new Date(Date.UTC(yy, (mm || 1) - 1, 1));
    return new Intl.DateTimeFormat("es-CL", {
      month: "short",
      year: "2-digit",
      timeZone: "UTC",
    }).format(dateUtc);
  };
  return (
    <div style={{ position: "relative", width }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <line
          x1={padding}
          y1={yZero}
          x2={width - padding}
          y2={yZero}
          stroke="rgba(0,0,0,.2)"
        />
        {monthly.map((m, idx) => {
          const x = padding + idx * (barW + gap);
          const aportes = Math.max(0, Number(m.aportes || 0));
          const retornoVal = Number(m.retorno || 0);
          const retornoPos = Math.max(0, retornoVal);
          const retornoNeg = Math.max(0, -retornoVal);
          const rescates = Math.max(0, Number(m.rescates || 0));
          const hasAny =
            aportes > 0 || retornoPos > 0 || rescates > 0 || retornoNeg > 0;
          const posH1 = scale(aportes);
          const posH2 = scale(retornoPos);
          const negHRes = scale(rescates);
          const negHRet = scale(retornoNeg);
          const topRet = yZero - posH1 - posH2;
          const net = aportes + retornoVal - rescates;
          const cy = yZero - (net >= 0 ? scale(net) : -scale(net));
          const isSelected = selectedMonth && m.month === selectedMonth;
          return (
            <g key={m.month}>
              {isSelected && (
                <rect
                  x={x - 6}
                  y={padding - 6}
                  width={barW + 12}
                  height={innerH + 12}
                  fill="none"
                  stroke="var(--primary-color, #3b82f6)"
                  strokeWidth="1.5"
                  rx="8"
                />
              )}
              {/* placeholder line for months with zero values to ensure visibility */}
              {!hasAny && (
                <rect
                  x={x}
                  y={yZero - 1}
                  width={barW}
                  height={2}
                  fill="rgba(107,114,128,.5)"
                  rx="1"
                />
              )}
              {/* aportes (azul) */}
              {aportes > 0 && (
                <rect
                  x={x}
                  y={yZero - posH1}
                  width={barW}
                  height={posH1}
                  fill="#3b82f6"
                  rx="3"
                  onMouseEnter={(e) =>
                    setHover({
                      type: "Aportes",
                      value: aportes,
                      x: x + barW / 2,
                      y: yZero - posH1 - 8,
                      month: m.month,
                    })
                  }
                  onMouseLeave={() => setHover(null)}
                />
              )}
              {/* retorno positivo (verde) */}
              {retornoPos > 0 && (
                <rect
                  x={x}
                  y={topRet}
                  width={barW}
                  height={posH2}
                  fill="#10b981"
                  rx="3"
                  onMouseEnter={(e) =>
                    setHover({
                      type: "Retorno",
                      value: retornoPos,
                      x: x + barW / 2,
                      y: topRet - 8,
                      month: m.month,
                    })
                  }
                  onMouseLeave={() => setHover(null)}
                />
              )}
              {/* retiros (naranjo) */}
              {rescates > 0 && (
                <rect
                  x={x}
                  y={yZero}
                  width={barW}
                  height={negHRes}
                  fill="#f59e0b"
                  rx="3"
                  onMouseEnter={(e) =>
                    setHover({
                      type: "Retiros",
                      value: -rescates,
                      x: x + barW / 2,
                      y: yZero + negHRes + 8,
                      month: m.month,
                    })
                  }
                  onMouseLeave={() => setHover(null)}
                />
              )}
              {/* retorno negativo (rojo) */}
              {retornoNeg > 0 && (
                <rect
                  x={x}
                  y={yZero + negHRes}
                  width={barW}
                  height={negHRet}
                  fill="#ef4444"
                  rx="3"
                  onMouseEnter={(e) =>
                    setHover({
                      type: "Retorno",
                      value: -retornoNeg,
                      x: x + barW / 2,
                      y: yZero + negHRes + negHRet + 8,
                      month: m.month,
                    })
                  }
                  onMouseLeave={() => setHover(null)}
                />
              )}
              {/* marcador neto (rombo) */}
              {Number.isFinite(cy) && (
                <path
                  d={`M ${x + barW / 2} ${cy - 5} L ${
                    x + barW / 2 + 5
                  } ${cy} L ${x + barW / 2} ${cy + 5} L ${
                    x + barW / 2 - 5
                  } ${cy} Z`}
                  fill="#111827"
                  opacity="0.9"
                  onMouseEnter={(e) =>
                    setHover({
                      type: "Neto",
                      value: net,
                      x: x + barW / 2,
                      y: cy - 10,
                      month: m.month,
                    })
                  }
                  onMouseLeave={() => setHover(null)}
                />
              )}
              {/* etiqueta mes */}
              <text
                x={x + barW / 2}
                y={height - 8}
                textAnchor="middle"
                style={{ fontSize: 11, fill: "rgba(0,0,0,.7)" }}
              >
                {toLabel(m.month)}
              </text>
            </g>
          );
        })}
      </svg>
      {hover && (
        <div
          className="chart-tooltip"
          style={{
            position: "absolute",
            left: Math.min(Math.max(hover.x - 60, 8), width - 160),
            top: Math.max(hover.y - 28, 8),
          }}
        >
          <div className="tt-row">
            {hover.type}: {fmt(hover.value)}
          </div>
        </div>
      )}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginTop: 6,
          minHeight: legendReservedHeight,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              background: "#3b82f6",
              display: "inline-block",
              borderRadius: 2,
            }}
          />
          Aportes
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              background: "#10b981",
              display: "inline-block",
              borderRadius: 2,
            }}
          />
          Retorno +
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              background: "#ef4444",
              display: "inline-block",
              borderRadius: 2,
            }}
          />
          Retorno -
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              background: "#f59e0b",
              display: "inline-block",
              borderRadius: 2,
            }}
          />
          Retiros
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              background: "#111827",
              clipPath: "polygon(50% 0, 100% 50%, 50% 100%, 0 50%)",
              display: "inline-block",
            }}
          />
          Neto
        </span>
      </div>
    </div>
  );
}

function TotalStackedBar({
  totals,
  height = 240,
  scaleMax = 1,
  label = "Total",
  legendReservedHeight = 0,
}) {
  const [hover, setHover] = React.useState(null); // { type, value, x, y }
  const fmt = (n) =>
    new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  const padding = 32;
  const barW = 40;
  const width = 140;
  const innerH = height - padding * 2;
  const halfH = innerH / 2;
  const yZero = padding + halfH;
  const aportes = Math.max(0, Number(totals?.aportes || 0));
  const retornoVal = Number(totals?.retorno || 0);
  const retornoPos = Math.max(0, retornoVal);
  const retornoNeg = Math.max(0, -retornoVal);
  const rescates = Math.max(0, Number(totals?.rescates || 0));
  const net = aportes + retornoVal - rescates;
  const scale = (v) =>
    (Math.abs(Number(v) || 0) / Math.max(1, scaleMax)) * halfH;
  const posH1 = scale(aportes);
  const posH2 = scale(retornoPos);
  const negHRes = scale(rescates);
  const negHRet = scale(retornoNeg);
  const topRet = yZero - posH1 - posH2;
  const cy = yZero - (net >= 0 ? scale(net) : -scale(net));
  const x = (width - barW) / 2;
  return (
    <div style={{ position: "relative", width }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <line
          x1={padding / 2}
          y1={yZero}
          x2={width - padding / 2}
          y2={yZero}
          stroke="rgba(0,0,0,.2)"
        />
        {aportes > 0 && (
          <rect
            x={x}
            y={yZero - posH1}
            width={barW}
            height={posH1}
            fill="#3b82f6"
            rx="3"
            onMouseEnter={() =>
              setHover({
                type: "Aportes",
                value: aportes,
                x: x + barW / 2,
                y: yZero - posH1 - 8,
              })
            }
            onMouseLeave={() => setHover(null)}
          />
        )}
        {retornoPos > 0 && (
          <rect
            x={x}
            y={topRet}
            width={barW}
            height={posH2}
            fill="#10b981"
            rx="3"
            onMouseEnter={() =>
              setHover({
                type: "Retorno",
                value: retornoPos,
                x: x + barW / 2,
                y: topRet - 8,
              })
            }
            onMouseLeave={() => setHover(null)}
          />
        )}
        {rescates > 0 && (
          <rect
            x={x}
            y={yZero}
            width={barW}
            height={negHRes}
            fill="#f59e0b"
            rx="3"
            onMouseEnter={() =>
              setHover({
                type: "Rescates",
                value: -rescates,
                x: x + barW / 2,
                y: yZero + negHRes + 8,
              })
            }
            onMouseLeave={() => setHover(null)}
          />
        )}
        {retornoNeg > 0 && (
          <rect
            x={x}
            y={yZero + negHRes}
            width={barW}
            height={negHRet}
            fill="#ef4444"
            rx="3"
            onMouseEnter={() =>
              setHover({
                type: "Retorno",
                value: -retornoNeg,
                x: x + barW / 2,
                y: yZero + negHRes + negHRet + 8,
              })
            }
            onMouseLeave={() => setHover(null)}
          />
        )}
        {Number.isFinite(cy) && (
          <path
            d={`M ${x + barW / 2} ${cy - 5} L ${x + barW / 2 + 5} ${cy} L ${
              x + barW / 2
            } ${cy + 5} L ${x + barW / 2 - 5} ${cy} Z`}
            fill="#111827"
            opacity="0.9"
            onMouseEnter={() =>
              setHover({
                type: "Neto",
                value: net,
                x: x + barW / 2,
                y: cy - 10,
              })
            }
            onMouseLeave={() => setHover(null)}
          />
        )}
        <text
          x={width / 2}
          y={height - 8}
          textAnchor="middle"
          style={{ fontSize: 11, fill: "rgba(0,0,0,.7)" }}
        >
          {label}
        </text>
      </svg>
      {hover && (
        <div
          className="chart-tooltip"
          style={{
            position: "absolute",
            left: Math.min(Math.max(hover.x - 60, 8), width - 160),
            top: Math.max(hover.y - 28, 8),
          }}
        >
          <div className="tt-row">
            {hover.type}: {fmt(hover.value)}
          </div>
        </div>
      )}
      <div style={{ minHeight: legendReservedHeight }}></div>
    </div>
  );
}

function DonutChart({ data, size = 180 }) {
  const total = (data || []).reduce(
    (acc, d) => acc + (Number(d.value) || 0),
    0
  );
  if (!total) return <div className="chart-empty">Sin datos</div>;
  const radius = size / 2;
  const stroke = 22;
  const cx = radius;
  const cy = radius;
  let offset = 0;
  const segments = (data || []).map((d, idx) => {
    const val = Number(d.value) || 0;
    const frac = val / total;
    const dash = Math.max(0.0001, frac * (Math.PI * (size - stroke)));
    const seg = (
      <circle
        key={idx}
        r={(size - stroke) / 2}
        cx={cx}
        cy={cy}
        fill="transparent"
        stroke={`hsl(${(idx * 53) % 360} 70% 50%)`}
        strokeWidth={stroke}
        strokeDasharray={`${dash} ${Math.PI * (size - stroke)}`}
        strokeDashoffset={-offset}
        transform={`rotate(-90 ${cx} ${cy})`}
      />
    );
    offset += dash;
    return seg;
  });
  return (
    <div className="donut-wrapper" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        {segments}
      </svg>
    </div>
  );
}

function PortfolioPage() {
  const { currentUser } = useAuth();
  const { currentEntity, initialized: entitiesInitialized } = useEntity();
  const { config: portfolioConfig } = usePortfolioConfig();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDateId, setSelectedDateId] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // 'overview' | 'positions' | 'consolidated'
  // Consolidated state (lazy)
  const [cLoading, setCLoading] = useState(false);
  const [cError, setCError] = useState("");
  const [cLatest, setCLatest] = useState(null);
  const [cHistory, setCHistory] = useState([]);
  const [cSelectedDateId, setCSelectedDateId] = useState(null);
  const [cSelectedDoc, setCSelectedDoc] = useState(null);
  const [isConfigOpen, setConfigOpen] = useState(false);
  // Single view: classic only
  // Removed annual tab; single view with monthly sequence + annual totals on the right

  useEffect(() => {
    async function load() {
      if (!currentUser || !currentEntity) return;
      setLoading(true);
      setError("");
      try {
        const [{ years }, l] = await Promise.all([
          getPortfolioYearRange(currentUser.uid, currentEntity.id),
          getLatestPortfolio(currentUser.uid, currentEntity.id),
        ]);
        const initYear =
          years && years.length ? years[0] : new Date().getFullYear();
        setAvailableYears(years || []);
        setSelectedYear(initYear);
        const h = await getPortfolioHistoryForYear(
          currentUser.uid,
          currentEntity.id,
          initYear
        );
        const historyToUse =
          h && h.length > 0
            ? h
            : await getPortfolioHistory(currentUser.uid, currentEntity.id, 365);
        setHistory(historyToUse || []);
        setLatest(l);
        setSelectedDateId(null);
        setSelectedDoc(null);
      } catch (e) {
        setError(e.message || "Error cargando portafolio");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [currentUser, currentEntity]);

  // Reload history when year changes
  useEffect(() => {
    let cancelled = false;
    async function reloadByYear() {
      if (!currentUser || !currentEntity || !selectedYear) return;
      try {
        setLoading(true);
        const h = await getPortfolioHistoryForYear(
          currentUser.uid,
          currentEntity.id,
          selectedYear
        );
        if (!cancelled) {
          const historyToUse = h && h.length > 0 ? h : [];
          setHistory(historyToUse);
          setSelectedDateId(
            historyToUse.length
              ? historyToUse[historyToUse.length - 1].id
              : null
          );
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Error cargando año");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    reloadByYear();
    return () => {
      cancelled = true;
    };
  }, [selectedYear, currentUser, currentEntity]);

  // Fetch document for selected date
  useEffect(() => {
    let cancelled = false;
    async function fetchByDate() {
      if (!currentUser || !currentEntity) return;
      if (!selectedDateId) {
        setSelectedDoc(null);
        return;
      }
      try {
        const d = await getPortfolioByDate(
          currentUser.uid,
          currentEntity.id,
          selectedDateId
        );
        if (!cancelled) setSelectedDoc(d);
      } catch (e) {
        if (!cancelled)
          setError(e.message || "Error obteniendo fecha seleccionada");
      }
    }
    fetchByDate();
    return () => {
      cancelled = true;
    };
  }, [selectedDateId, currentUser, currentEntity]);

  // Lazy-load consolidated data when consolidated tab becomes active
  useEffect(() => {
    let cancelled = false;
    async function loadConsolidated() {
      if (activeTab !== "consolidated") return;
      if (!currentUser || !currentEntity) return;
      setCLoading(true);
      setCError("");
      try {
        const [latestC, historyC] = await Promise.all([
          getConsolidatedLatest(currentUser.uid, currentEntity.id),
          getConsolidatedHistory(currentUser.uid, currentEntity.id, 60),
        ]);
        if (cancelled) return;
        // Filter consolidated history to show only dates where both providers have data
        // Sura data is available from 2025-09-04 onwards
        const filteredHistory = (historyC || []).filter((h) => {
          const dateId = h.id || "";
          return dateId >= "2025-09-04";
        });
        setCLatest(latestC || null);
        setCHistory(filteredHistory);
        setCSelectedDateId(null);
        setCSelectedDoc(null);
      } catch (e) {
        if (!cancelled) setCError(e.message || "Error cargando consolidado");
      } finally {
        if (!cancelled) setCLoading(false);
      }
    }
    loadConsolidated();
    return () => {
      cancelled = true;
    };
  }, [activeTab, currentUser, currentEntity]);

  // Fetch consolidated doc when a consolidated date is selected
  useEffect(() => {
    let cancelled = false;
    async function fetchConsolidatedByDate() {
      if (activeTab !== "consolidated") return;
      if (!currentUser || !currentEntity) return;
      if (!cSelectedDateId) {
        setCSelectedDoc(null);
        return;
      }
      try {
        const d = await getConsolidatedByDate(
          currentUser.uid,
          currentEntity.id,
          cSelectedDateId
        );
        if (!cancelled) setCSelectedDoc(d);
      } catch (e) {
        if (!cancelled)
          setCError(
            e.message || "Error obteniendo fecha seleccionada (consolidado)"
          );
      }
    }
    fetchConsolidatedByDate();
    return () => {
      cancelled = true;
    };
  }, [activeTab, cSelectedDateId, currentUser, currentEntity]);

  const series = useMemo(() => buildNetWorthSeries(history), [history]);
  const active = selectedDoc || latest;
  const ytd = useMemo(
    () => computeReturnYTD(history, active?.id),
    [history, active?.id]
  );
  const mtd = useMemo(
    () => computeReturnMTD(history, active?.id),
    [history, active?.id]
  );
  const allocation = useMemo(() => buildProductAllocation(active), [active]);
  const currencies = useMemo(() => buildCurrencyBreakdown(active), [active]);
  const positions = useMemo(() => buildPositions(active), [active]);
  const patrimonio = Number(active?.portfolio?.summary?.patrimonioTotal || 0);
  const sinInvertir = useMemo(() => {
    const fromAlloc = extractCashFromAllocationsCI(active);
    const fromSummary = Number(active?.portfolio?.summary?.sinInvertir || 0);
    return Number.isFinite(fromAlloc) && fromAlloc > 0
      ? fromAlloc
      : fromSummary;
  }, [active]);

  // Dev logs removed

  // Build monthly aggregates (YYYY-MM) for aportes, rescates, retorno up to cutoff date
  const monthly = useMemo(() => {
    if (!history || history.length === 0) return [];
    const cutoffId =
      selectedDateId || active?.id || history[history.length - 1]?.id;
    const map = new Map();
    // Consider only snapshots up to cutoff (inclusive)
    const filtered = history.filter((h) => !cutoffId || h.id <= cutoffId);
    filtered.forEach((h) => {
      const month = h.id.slice(0, 7); // YYYY-MM
      const sum = h.portfolio?.summary || {};
      const cur = map.get(month) || {
        month,
        aportes: 0,
        rescates: 0,
        retorno: 0,
        _lastDay: "",
      };
      if (!cur._lastDay || h.id > cur._lastDay) {
        cur._lastDay = h.id;
        cur.aportes = Number(sum.abonosMes || 0);
        cur.rescates = Number(sum.retirosMes || 0);
        cur.retorno = Number(sum.gananciaPerdidaMes || 0);
      }
      map.set(month, cur);
    });
    return Array.from(map.values())
      .map(({ _lastDay, ...rest }) => rest)
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [history, active?.id, selectedDateId]);

  // Build yearly aggregates from monthly values
  const yearly = useMemo(() => {
    if (!monthly || monthly.length === 0) return [];
    const map = new Map();
    monthly.forEach((m) => {
      const year = m.month.slice(0, 4);
      const cur = map.get(year) || {
        year,
        aportes: 0,
        rescates: 0,
        retorno: 0,
      };
      cur.aportes += Number(m.aportes || 0);
      cur.rescates += Number(m.rescates || 0);
      cur.retorno += Number(m.retorno || 0);
      map.set(year, cur);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.year.localeCompare(b.year)
    );
  }, [monthly]);

  // Precompute scale maxima to keep consistent vertical scale between sequence and total bar
  // Unified vertical scale: ensure both monthly sequence and total bar share the larger magnitude
  const monthlyScaleMax = useMemo(() => {
    if (!monthly || monthly.length === 0) return 1;
    const posMax = Math.max(
      ...monthly.map((m) => {
        const aportes = Math.max(0, Number(m.aportes || 0));
        const retornoVal = Number(m.retorno || 0);
        const retornoPos = Math.max(0, retornoVal);
        return aportes + retornoPos;
      })
    );
    const negMax = Math.max(
      ...monthly.map((m) => {
        const rescates = Math.max(0, Number(m.rescates || 0));
        const retornoVal = Number(m.retorno || 0);
        const retornoNeg = Math.max(0, -retornoVal);
        return rescates + retornoNeg;
      })
    );
    return Math.max(1, posMax, negMax);
  }, [monthly]);

  const yearlyScaleMax = useMemo(() => {
    if (!yearly || yearly.length === 0) return 1;
    const posMax = Math.max(
      ...yearly.map((y) => {
        const aportes = Math.max(0, Number(y.aportes || 0));
        const retornoVal = Number(y.retorno || 0);
        const retornoPos = Math.max(0, retornoVal);
        return aportes + retornoPos;
      })
    );
    const negMax = Math.max(
      ...yearly.map((y) => {
        const rescates = Math.max(0, Number(y.rescates || 0));
        const retornoVal = Number(y.retorno || 0);
        const retornoNeg = Math.max(0, -retornoVal);
        return rescates + retornoNeg;
      })
    );
    return Math.max(1, posMax, negMax);
  }, [yearly]);

  const unifiedScaleMax = useMemo(() => {
    return Math.max(1, monthlyScaleMax, yearlyScaleMax);
  }, [monthlyScaleMax, yearlyScaleMax]);

  // Totals for the right-side bar (sum over the visible sequence)
  const barTotals = useMemo(() => {
    // Totales sobre toda la secuencia mensual mostrada (año actual u histórico cargado)
    return (monthly || []).reduce(
      (acc, m) => ({
        aportes: acc.aportes + Number(m.aportes || 0),
        rescates: acc.rescates + Number(m.rescates || 0),
        retorno: acc.retorno + Number(m.retorno || 0),
      }),
      { aportes: 0, rescates: 0, retorno: 0 }
    );
  }, [monthly]);

  // Legend reserved heights to keep plots aligned without wasting space
  const donutLegendReserved = useMemo(() => {
    const rowsLeft = (allocation || []).length;
    const rowsRight = (currencies || []).length;
    const rows = Math.max(rowsLeft, rowsRight);
    const rowHeight = 20;
    const extra = 8;
    return rows * rowHeight + extra;
  }, [allocation, currencies]);

  const barLegendReserved = 20; // single-row legend height
  // debugDonut removed

  // Aggregates for summary table (YTD / MTD)
  const mtdAgg = useMemo(() => {
    if (!history || history.length === 0 || !active?.id)
      return {
        initial: 0,
        aportes: 0,
        retiros: 0,
        retorno: 0,
        final: patrimonio,
      };
    const base = new Date((active.id || "") + "T00:00:00Z");
    if (isNaN(base.getTime()))
      return {
        initial: 0,
        aportes: 0,
        retiros: 0,
        retorno: 0,
        final: patrimonio,
      };
    const year = base.getUTCFullYear();
    const month = base.getUTCMonth();
    const monthStartId = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const beforeMonth = history.filter((h) => h.id < monthStartId);
    const inMonth = history.filter(
      (h) => h.id >= monthStartId && h.id <= active.id
    );
    const baseSnapshot =
      beforeMonth.length > 0
        ? beforeMonth[beforeMonth.length - 1]
        : inMonth.length > 0
        ? inMonth[0]
        : null;
    const initial = Number(
      baseSnapshot?.portfolio?.summary?.patrimonioTotal || 0
    );
    const aportes = Number(active?.portfolio?.summary?.abonosMes || 0);
    const retiros = Number(active?.portfolio?.summary?.retirosMes || 0);
    const retorno = Number(mtd.amount || 0);
    const finalV = Number(active?.portfolio?.summary?.patrimonioTotal || 0);
    return { initial, aportes, retiros, retorno, final: finalV };
  }, [history, active?.id, active?.portfolio?.summary, mtd.amount, patrimonio]);

  const ytdAgg = useMemo(() => {
    if (!history || history.length === 0 || !active?.id)
      return {
        initial: 0,
        aportes: 0,
        retiros: 0,
        retorno: 0,
        final: patrimonio,
      };
    const [yy] = (active.id || "").split("-");
    const targetYear = yy;
    if (!targetYear)
      return {
        initial: 0,
        aportes: 0,
        retiros: 0,
        retorno: 0,
        final: patrimonio,
      };
    const yearStartId = `${targetYear}-01-01`;
    const beforeYear = history.filter((h) => h.id < yearStartId);
    const inYear = history.filter(
      (h) => h.id >= yearStartId && h.id <= active.id
    );
    const baseSnapshot =
      beforeYear.length > 0
        ? beforeYear[beforeYear.length - 1]
        : inYear.length > 0
        ? inYear[0]
        : null;
    const initial = Number(
      baseSnapshot?.portfolio?.summary?.patrimonioTotal || 0
    );
    const aportes = (monthly || [])
      .filter((m) => m.month.startsWith(targetYear + "-"))
      .reduce((s, m) => s + Number(m.aportes || 0), 0);
    const retiros = (monthly || [])
      .filter((m) => m.month.startsWith(targetYear + "-"))
      .reduce((s, m) => s + Number(m.rescates || 0), 0);
    const retorno = Number(ytd.amount || 0);
    const finalV = Number(active?.portfolio?.summary?.patrimonioTotal || 0);
    return { initial, aportes, retiros, retorno, final: finalV };
  }, [
    history,
    active?.id,
    active?.portfolio?.summary,
    monthly,
    ytd.amount,
    patrimonio,
  ]);

  // Group positions by account and compute totals
  const positionsByAccount = useMemo(() => {
    const map = new Map();
    (positions || []).forEach((pos) => {
      const key = pos.account || "Cuenta";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(pos);
    });
    return map;
  }, [positions]);

  const sortedAccounts = useMemo(() => {
    const accounts = Array.from(positionsByAccount.keys());
    // Prefer accounts that look like investment accounts first
    const score = (name) => (/inver/i.test(name) ? 1 : 0);
    return accounts.sort((a, b) => score(b) - score(a) || a.localeCompare(b));
  }, [positionsByAccount]);

  // Get active data sources for display
  const activeDataSources = useMemo(() => {
    const sources = [];
    if (portfolioConfig.dataSources.bice) sources.push("Banco Bice");
    if (portfolioConfig.dataSources.sura) sources.push("Sura");
    return sources;
  }, [portfolioConfig.dataSources]);

  return (
    <div className="portfolio-page">
      <Header
        currentPage="portfolio"
        pageTitle="Investment Portfolio"
        titleSuffix={
          <>
            {active?.id && (
              <div
                className="asof"
                style={{
                  padding: "2px 8px",
                  borderRadius: 6,
                  background: "var(--panel-bg, rgba(0,0,0,0.06))",
                  fontSize: 13,
                }}
              >
                Corte: {active.id}
              </div>
            )}
            {selectedDateId && (
              <button
                onClick={() => {
                  setSelectedDateId(null);
                  setSelectedDoc(null);
                }}
                className="btn-reset"
                style={{
                  marginLeft: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "2px 8px",
                  borderRadius: 6,
                }}
                title="Limpiar fecha seleccionada"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Reset
              </button>
            )}
            {activeDataSources.length > 0 && (
              <div
                className="data-sources-indicator"
                style={{
                  marginLeft: 8,
                  padding: "2px 8px",
                  borderRadius: 6,
                  background: "var(--success-bg, rgba(16, 185, 129, 0.1))",
                  border:
                    "1px solid var(--success-border, rgba(16, 185, 129, 0.3))",
                  fontSize: 12,
                  color: "var(--success-text, #059669)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span>📊</span>
                <span>{activeDataSources.join(", ")}</span>
              </div>
            )}
            <button
              className="btn-secondary"
              style={{ marginLeft: 8 }}
              title="Configuración de fuentes de datos"
              onClick={() => setConfigOpen(true)}
            >
              ⚙️
            </button>
          </>
        }
      />
      <div
        className="portfolio-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          margin: "0 0 12px",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div
            className="tabs"
            style={{
              display: "inline-flex",
              gap: 6,
              background: "var(--panel-bg, rgba(0,0,0,0.04))",
              padding: 4,
              borderRadius: 8,
            }}
          >
            <button
              className={`tab ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => setActiveTab("overview")}
              style={{ padding: "6px 10px", borderRadius: 6 }}
            >
              Resumen
            </button>
            <button
              className={`tab ${activeTab === "positions" ? "active" : ""}`}
              onClick={() => setActiveTab("positions")}
              style={{ padding: "6px 10px", borderRadius: 6 }}
            >
              Posiciones
            </button>
            <button
              className={`tab ${activeTab === "consolidated" ? "active" : ""}`}
              onClick={() => setActiveTab("consolidated")}
              style={{ padding: "6px 10px", borderRadius: 6 }}
            >
              Consolidado
            </button>
          </div>
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <label
            htmlFor="yearSelect"
            style={{ fontSize: 13, color: "var(--muted-fg, #555)" }}
          >
            Año
          </label>
          <select
            id="yearSelect"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {(availableYears.length
              ? availableYears
              : [new Date().getFullYear()]
            ).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Single-view layout */}

      {!currentUser && (
        <div className="state error">
          Debes iniciar sesión para ver el portafolio.
        </div>
      )}
      {currentUser && !entitiesInitialized && (
        <div className="state">Cargando entidades…</div>
      )}
      {currentUser && entitiesInitialized && !currentEntity && (
        <div className="state error">
          No hay entidad seleccionada. Ve a Configuración o Finanzas para
          crear/seleccionar una entidad.
        </div>
      )}

      {loading && <div className="state">Cargando…</div>}
      {error && <div className="state error">{error}</div>}

      {!loading && currentUser && currentEntity && !latest && (
        <div className="state">
          Aún no hay datos del portafolio para hoy. Ejecuta la sincronización o
          vuelve más tarde.
        </div>
      )}

      {!loading && latest && (
        <>
          {activeDataSources.length === 0 && (
            <div
              className="state warning"
              style={{
                background: "var(--warning-bg, #fef3c7)",
                border: "1px solid var(--warning-border, #f59e0b)",
                color: "var(--warning-text, #92400e)",
                marginBottom: 16,
              }}
            >
              ⚠️ No hay fuentes de datos activas. Ve a la configuración para
              seleccionar las fuentes de datos del portafolio.
            </div>
          )}
          {sinInvertir > 0 && (
            <div className="cash-warning">
              ⚠️ Caja disponible: {formatCurrency(sinInvertir)}
            </div>
          )}
          {activeTab === "overview" && (
            <>
              <div className="kpi-row">
                <div className="kpi-card">
                  <div className="kpi-label">Patrimonio</div>
                  <div className="kpi-value">{formatCurrency(patrimonio)}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">YTD</div>
                  <div className="kpi-value">
                    {formatCurrency(ytd.amount)}{" "}
                    <span className={ytd.amount >= 0 ? "pos" : "neg"}>
                      ({percentText(ytd.percent)})
                    </span>
                  </div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">MTD</div>
                  <div className="kpi-value">
                    {formatCurrency(mtd.amount)}{" "}
                    <span className={mtd.amount >= 0 ? "pos" : "neg"}>
                      ({percentText(mtd.percent)})
                    </span>
                  </div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">Caja</div>
                  <div className="kpi-value">{formatCurrency(sinInvertir)}</div>
                </div>
              </div>
              {/* Row 1: Evolución (izq) + Resumen Mensual (der) */}
              <div className="grid-2">
                <div className="panel">
                  <div className="panel-header">Evolución Patrimonio</div>
                  <LineChart
                    data={series}
                    selectedDate={selectedDateId}
                    onDateSelect={setSelectedDateId}
                  />
                </div>
                <div className="panel">
                  <div className="table-wrap">
                    <table className="positions-table">
                      <thead>
                        <tr>
                          <th>Concepto</th>
                          <th className="num">MTD</th>
                          <th className="num">YTD</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="highlight initial">
                          <td>Patrimonio inicial</td>
                          <td className="num">
                            {formatCurrency(mtdAgg.initial)}
                          </td>
                          <td className="num">
                            {formatCurrency(ytdAgg.initial)}
                          </td>
                        </tr>
                        <tr>
                          <td>Aportes</td>
                          <td className="num">
                            {formatCurrency(mtdAgg.aportes)}
                          </td>
                          <td className="num">
                            {formatCurrency(ytdAgg.aportes)}
                          </td>
                        </tr>
                        <tr>
                          <td>Retiros</td>
                          <td className="num">
                            {formatCurrency(mtdAgg.retiros)}
                          </td>
                          <td className="num">
                            {formatCurrency(ytdAgg.retiros)}
                          </td>
                        </tr>
                        <tr className="highlight net">
                          <td>Aporte neto</td>
                          <td className="num">
                            {formatCurrency(
                              (mtdAgg.aportes || 0) - (mtdAgg.retiros || 0)
                            )}
                          </td>
                          <td className="num">
                            {formatCurrency(
                              (ytdAgg.aportes || 0) - (ytdAgg.retiros || 0)
                            )}
                          </td>
                        </tr>
                        <tr className="highlight return">
                          <td>Rentabilidad</td>
                          <td
                            className={`num ${
                              mtdAgg.retorno >= 0 ? "pos" : "neg"
                            }`}
                          >
                            {formatCurrency(mtdAgg.retorno)}
                          </td>
                          <td
                            className={`num ${
                              ytdAgg.retorno >= 0 ? "pos" : "neg"
                            }`}
                          >
                            {formatCurrency(ytdAgg.retorno)}
                          </td>
                        </tr>
                        <tr className="highlight final">
                          <td>Patrimonio final</td>
                          <td className="num">
                            {formatCurrency(mtdAgg.final)}
                          </td>
                          <td className="num">
                            {formatCurrency(ytdAgg.final)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              {/* Row 2: Donuts juntos (izq) + Barras apiladas (der) */}
              <div className="grid-2">
                <div
                  className="panel"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <div className="panel-header">Composición y Moneda</div>
                  <div
                    className="panel-content-flex"
                    style={{
                      flex: 1,
                      alignItems: "flex-end",
                      gap: 24,
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "50%",
                        paddingTop: 0,
                      }}
                    >
                      <DonutChart data={allocation} />
                      <div
                        className="legend below"
                        style={{ marginTop: 8, minHeight: donutLegendReserved }}
                      >
                        {allocation.map((a, i) => (
                          <div key={i} className="legend-row">
                            <span
                              className="badge"
                              style={{
                                backgroundColor: `hsl(${
                                  (i * 53) % 360
                                } 70% 50%)`,
                              }}
                            />
                            <span className="lbl">{a.label}</span>
                            <span className="val">
                              {formatCurrency(a.value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "50%",
                        paddingTop: 0,
                      }}
                    >
                      <DonutChart data={currencies} />
                      <div
                        className="legend below"
                        style={{
                          marginTop: 8,
                          textAlign: "right",
                          minHeight: donutLegendReserved,
                        }}
                      >
                        {currencies.map((c, i) => (
                          <div key={i} className="legend-row">
                            <span
                              className="badge"
                              style={{
                                backgroundColor: `hsl(${
                                  (i * 53) % 360
                                } 70% 50%)`,
                              }}
                            />
                            <span className="lbl">{c.label}</span>
                            <span className="val">
                              {formatCurrency(c.value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="panel">
                  <div className="panel-header">
                    Flujo Mensual (Aportes, Retorno, Retiros)
                  </div>
                  <div className="panel-content-flex" style={{ width: "100%" }}>
                    <div
                      style={{
                        flex: "0 0 83.333%",
                        overflowX: "auto",
                        paddingTop: 0,
                      }}
                    >
                      <MonthlyStackedChart
                        monthly={monthly}
                        selectedMonth={(
                          selectedDateId ||
                          active?.id ||
                          ""
                        ).slice(0, 7)}
                        scaleMax={unifiedScaleMax}
                        legendReservedHeight={barLegendReserved}
                      />
                    </div>
                    <div
                      style={{
                        flex: "0 0 16.666%",
                        display: "flex",
                        justifyContent: "flex-end",
                        paddingTop: 0,
                      }}
                    >
                      <TotalStackedBar
                        totals={barTotals}
                        height={240}
                        scaleMax={unifiedScaleMax}
                        legendReservedHeight={barLegendReserved}
                        label="Total"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "positions" && (
            <div className="panel">
              <div className="panel-header">Posiciones</div>
              <div className="table-wrap">
                <table className="positions-table">
                  <thead>
                    <tr>
                      <th>Cuenta</th>
                      <th>Instrumento</th>
                      <th>Nemo</th>
                      <th>Moneda</th>
                      <th className="num">Títulos</th>
                      <th className="num">Precio</th>
                      <th className="num">Valorización</th>
                      <th className="pct">% del Patrimonio</th>
                      <th className="pct">Retorno</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAccounts.map((account) => {
                      const rows = positionsByAccount.get(account) || [];
                      const subtotalVal = rows.reduce(
                        (sum, r) => sum + Number(r.valorizacion || 0),
                        0
                      );
                      const subtotalPct =
                        patrimonio > 0 ? (subtotalVal / patrimonio) * 100 : 0;
                      return (
                        <React.Fragment key={account}>
                          {rows.map((p, i) => (
                            <tr key={`${account}-${i}`}>
                              <td>{p.account}</td>
                              <td>{p.nombre}</td>
                              <td>{p.nemo}</td>
                              <td>{p.moneda}</td>
                              <td className="num">
                                {p.titulos?.toLocaleString("es-CL")}
                              </td>
                              <td className="num">
                                {formatCurrency(p.precio)}
                              </td>
                              <td className="num">
                                {formatCurrency(p.valorizacion)}
                              </td>
                              <td className="pct">
                                {patrimonio > 0
                                  ? `${(
                                      (Number(p.valorizacion || 0) /
                                        patrimonio) *
                                      100
                                    ).toFixed(2)}%`
                                  : "0.00%"}
                              </td>
                              <td
                                className={`pct ${
                                  Number(p.rentabilidadPct) >= 0 ? "pos" : "neg"
                                }`}
                              >
                                {percentText(p.rentabilidadPct)}
                              </td>
                            </tr>
                          ))}
                          <tr className="subtotal-row">
                            <td colSpan={6} className="subtotal-label">
                              <strong>Subtotal {account}</strong>
                            </td>
                            <td className="num">
                              <strong>{formatCurrency(subtotalVal)}</strong>
                            </td>
                            <td className="pct">
                              <strong>{subtotalPct.toFixed(2)}%</strong>
                            </td>
                            <td></td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "consolidated" && (
            <div className="panel">
              <div className="panel-header">Portafolio Consolidado</div>
              {cLoading && <div className="state">Cargando consolidado…</div>}
              {cError && <div className="state error">{cError}</div>}
              {!cLoading && !cError && !cLatest && (
                <div className="state">No hay datos consolidados.</div>
              )}
              {!cLoading && !cError && (cLatest || cSelectedDoc) && (
                <>
                  {(() => {
                    const cActive = cSelectedDoc || cLatest;
                    const totals = cActive?.totals_consolidated || {};
                    const holdings = cActive?.holdings || [];
                    const accounts = cActive?.accounts_consolidated || [];
                    const asof = cActive?.id || cActive?.header?.dataDate;

                    // SHARED: Calculate base snapshots once for ALL calculations
                    const currentDate = cActive?.id || "";
                    let monthBase = null,
                      yearBase = null;

                    if (currentDate && cHistory && cHistory.length > 0) {
                      const currentDateObj = new Date(
                        currentDate + "T00:00:00Z"
                      );
                      const currentYear = currentDateObj.getUTCFullYear();
                      const currentMonth = currentDateObj.getUTCMonth() + 1;
                      const monthStartId = `${currentYear}-${String(
                        currentMonth
                      ).padStart(2, "0")}-01`;
                      const yearStartId = `${currentYear}-01-01`;

                      const beforeMonth = cHistory.filter(
                        (h) => h.id < monthStartId
                      );
                      const beforeYear = cHistory.filter(
                        (h) => h.id < yearStartId
                      );

                      monthBase =
                        beforeMonth.length > 0
                          ? beforeMonth[beforeMonth.length - 1]
                          : cHistory.find((h) => h.id >= monthStartId);
                      yearBase =
                        beforeYear.length > 0
                          ? beforeYear[beforeYear.length - 1]
                          : cHistory.find((h) => h.id >= yearStartId);
                    }

                    return (
                      <>
                        {(() => {
                          // Compute MTD and YTD for consolidated using shared base
                          let mtdAmount = 0,
                            ytdAmount = 0,
                            mtdPct = 0,
                            ytdPct = 0;

                          if (monthBase && yearBase) {
                            const currentValue = Number(totals.valueHoy || 0);
                            const monthBaseValue = Number(
                              monthBase?.totals_consolidated?.valueHoy || 0
                            );
                            const yearBaseValue = Number(
                              yearBase?.totals_consolidated?.valueHoy || 0
                            );

                            mtdAmount = currentValue - monthBaseValue;
                            ytdAmount = currentValue - yearBaseValue;
                            mtdPct =
                              monthBaseValue > 0
                                ? (mtdAmount / monthBaseValue) * 100
                                : 0;
                            ytdPct =
                              yearBaseValue > 0
                                ? (ytdAmount / yearBaseValue) * 100
                                : 0;
                          }

                          return (
                            <div
                              style={{
                                display: "flex",
                                gap: "12px",
                                flexWrap: "nowrap",
                                overflowX: "auto",
                                marginBottom: "16px",
                              }}
                            >
                              <div className="kpi-card">
                                <div className="kpi-label">Valor Hoy</div>
                                <div className="kpi-value">
                                  {formatCurrency(totals.valueHoy)}
                                </div>
                              </div>
                              <div className="kpi-card">
                                <div className="kpi-label">Δ Día</div>
                                <div className="kpi-value">
                                  {formatCurrency(totals.delta_valueCLP)}
                                </div>
                              </div>
                              <div className="kpi-card">
                                <div className="kpi-label">Retorno Diario</div>
                                <div className="kpi-value">
                                  {percentText(
                                    (totals.daily_return_pct || 0) * 100
                                  )}
                                </div>
                              </div>
                              <div className="kpi-card">
                                <div className="kpi-label">Activos</div>
                                <div className="kpi-value">
                                  {Number(
                                    totals.assetsCount || 0
                                  ).toLocaleString("es-CL")}
                                </div>
                              </div>
                              <div className="kpi-card">
                                <div className="kpi-label">MTD $</div>
                                <div className="kpi-value">
                                  {formatMillions(mtdAmount, 2)}
                                </div>
                              </div>
                              <div className="kpi-card">
                                <div className="kpi-label">MTD %</div>
                                <div className="kpi-value">
                                  <span
                                    className={mtdAmount >= 0 ? "pos" : "neg"}
                                  >
                                    {percentText(mtdPct, 1)}
                                  </span>
                                </div>
                              </div>
                              <div className="kpi-card">
                                <div className="kpi-label">YTD $</div>
                                <div className="kpi-value">
                                  {formatMillions(ytdAmount, 2)}
                                </div>
                              </div>
                              <div className="kpi-card">
                                <div className="kpi-label">YTD %</div>
                                <div className="kpi-value">
                                  <span
                                    className={ytdAmount >= 0 ? "pos" : "neg"}
                                  >
                                    {percentText(ytdPct, 1)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {Array.isArray(cHistory) && cHistory.length > 1 && (
                          <div className="grid-2" style={{ marginTop: 12 }}>
                            <div className="panel">
                              <div className="panel-header">
                                Evolución Consolidada
                              </div>
                              <LineChart
                                data={(cHistory || []).map((h) => ({
                                  date: h.id,
                                  value: Number(
                                    h?.totals_consolidated?.valueHoy || 0
                                  ),
                                }))}
                                selectedDate={cSelectedDateId}
                                onDateSelect={setCSelectedDateId}
                              />
                            </div>
                            <div className="panel">
                              <div className="panel-header">
                                Por Wealth Manager
                              </div>
                              <div className="table-wrap">
                                <table className="positions-table">
                                  <thead>
                                    <tr>
                                      <th>Gestora</th>
                                      <th className="num">AUM</th>
                                      <th className="pct">% Total</th>
                                      <th className="num">MTD $</th>
                                      <th className="pct">MTD %</th>
                                      <th className="num">YTD $</th>
                                      <th className="pct">YTD %</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(() => {
                                      const provs = Object.entries(
                                        cActive?.providers || {}
                                      );
                                      const totalAUM = Number(
                                        totals.valueHoy || 0
                                      );
                                      // Use shared monthBase and yearBase from parent scope

                                      // Compute aggregates for Total row
                                      let totalMTD = 0,
                                        totalYTD = 0;

                                      const rows = provs.map(
                                        ([provId, provObj]) => {
                                          const aum = Number(
                                            provObj?.totals
                                              ?.portfolioValueCLP || 0
                                          );
                                          const pct =
                                            totalAUM > 0
                                              ? (aum / totalAUM) * 100
                                              : 0;
                                          const label =
                                            provId.toLowerCase() === "bice"
                                              ? "BICE"
                                              : provId.toLowerCase() === "sura"
                                              ? "Sura"
                                              : provId.charAt(0).toUpperCase() +
                                                provId.slice(1);

                                          // Compute MTD and YTD for this provider using shared base snapshots
                                          let mtdAmount = 0,
                                            mtdPct = 0,
                                            ytdAmount = 0,
                                            ytdPct = 0;
                                          if (monthBase && yearBase) {
                                            const monthBaseValue = Number(
                                              monthBase?.providers?.[provId]
                                                ?.totals?.portfolioValueCLP || 0
                                            );
                                            const yearBaseValue = Number(
                                              yearBase?.providers?.[provId]
                                                ?.totals?.portfolioValueCLP || 0
                                            );

                                            mtdAmount = aum - monthBaseValue;
                                            ytdAmount = aum - yearBaseValue;
                                            mtdPct =
                                              monthBaseValue > 0
                                                ? (mtdAmount / monthBaseValue) *
                                                  100
                                                : 0;
                                            ytdPct =
                                              yearBaseValue > 0
                                                ? (ytdAmount / yearBaseValue) *
                                                  100
                                                : 0;
                                          }

                                          totalMTD += mtdAmount;
                                          totalYTD += ytdAmount;

                                          return (
                                            <tr key={provId}>
                                              <td>
                                                <strong>{label}</strong>
                                              </td>
                                              <td className="num">
                                                {formatMillions(aum, 2)}
                                              </td>
                                              <td className="pct">
                                                {percentText(pct, 1)}
                                              </td>
                                              <td className="num">
                                                {formatMillions(mtdAmount, 2)}
                                              </td>
                                              <td className="pct">
                                                <span
                                                  className={
                                                    mtdAmount >= 0
                                                      ? "pos"
                                                      : "neg"
                                                  }
                                                >
                                                  {percentText(mtdPct, 1)}
                                                </span>
                                              </td>
                                              <td className="num">
                                                {formatMillions(ytdAmount, 2)}
                                              </td>
                                              <td className="pct">
                                                <span
                                                  className={
                                                    ytdAmount >= 0
                                                      ? "pos"
                                                      : "neg"
                                                  }
                                                >
                                                  {percentText(ytdPct, 1)}
                                                </span>
                                              </td>
                                            </tr>
                                          );
                                        }
                                      );

                                      // Compute total row percentages using shared base
                                      let totalMTDPct = 0,
                                        totalYTDPct = 0;

                                      if (monthBase && yearBase) {
                                        const monthBaseConsolidated = Number(
                                          monthBase?.totals_consolidated
                                            ?.valueHoy || 0
                                        );
                                        const yearBaseConsolidated = Number(
                                          yearBase?.totals_consolidated
                                            ?.valueHoy || 0
                                        );

                                        totalMTDPct =
                                          monthBaseConsolidated > 0
                                            ? (totalMTD /
                                                monthBaseConsolidated) *
                                              100
                                            : 0;
                                        totalYTDPct =
                                          yearBaseConsolidated > 0
                                            ? (totalYTD /
                                                yearBaseConsolidated) *
                                              100
                                            : 0;
                                      }

                                      rows.push(
                                        <tr
                                          className="subtotal-row"
                                          key="total"
                                        >
                                          <td>
                                            <strong>Total</strong>
                                          </td>
                                          <td className="num">
                                            <strong>
                                              {formatMillions(totalAUM, 2)}
                                            </strong>
                                          </td>
                                          <td className="pct">
                                            <strong>
                                              {percentText(100, 1)}
                                            </strong>
                                          </td>
                                          <td className="num">
                                            <strong>
                                              {formatMillions(totalMTD, 2)}
                                            </strong>
                                          </td>
                                          <td className="pct">
                                            <strong>
                                              <span
                                                className={
                                                  totalMTD >= 0 ? "pos" : "neg"
                                                }
                                              >
                                                {percentText(totalMTDPct, 1)}
                                              </span>
                                            </strong>
                                          </td>
                                          <td className="num">
                                            <strong>
                                              {formatMillions(totalYTD, 2)}
                                            </strong>
                                          </td>
                                          <td className="pct">
                                            <strong>
                                              <span
                                                className={
                                                  totalYTD >= 0 ? "pos" : "neg"
                                                }
                                              >
                                                {percentText(totalYTDPct, 1)}
                                              </span>
                                            </strong>
                                          </td>
                                        </tr>
                                      );

                                      return rows;
                                    })()}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="panel" style={{ marginTop: 12 }}>
                          <div className="panel-header">Cuentas</div>
                          <div className="table-wrap">
                            <table className="positions-table">
                              <thead>
                                <tr>
                                  <th>Wealth Manager</th>
                                  <th>Cuenta</th>
                                  <th className="num">
                                    Cantidad de instrumentos
                                  </th>
                                  <th className="num">Monto</th>
                                  <th className="pct">Retorno Diario</th>
                                  <th className="num">Δ Día</th>
                                  <th className="pct">% del Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  const totalsValue = Number(
                                    totals.valueHoy || 0
                                  );
                                  const fmtProvider = (id) => {
                                    if (!id) return "—";
                                    if (String(id).toLowerCase() === "bice")
                                      return "BICE";
                                    if (String(id).toLowerCase() === "sura")
                                      return "Sura";
                                    return (
                                      String(id).charAt(0).toUpperCase() +
                                      String(id).slice(1)
                                    );
                                  };
                                  const pickProviderForAccount = (name) => {
                                    const provs = Object.entries(
                                      cActive?.providers || {}
                                    );
                                    let bestId = null;
                                    let bestVal = 0;
                                    for (const [provId, provObj] of provs) {
                                      const acc = (
                                        provObj?.accounts || []
                                      ).find((x) => x.accountName === name);
                                      const v = Number(acc?.valueHoy || 0);
                                      if (v > bestVal) {
                                        bestVal = v;
                                        bestId = provId;
                                      }
                                    }
                                    return bestId;
                                  };
                                  const rows = (accounts || []).filter(
                                    (a) => Number(a?.valueHoy || 0) > 0
                                  );
                                  const totalInstruments = rows.reduce(
                                    (s, a) => s + Number(a.assetsCount || 0),
                                    0
                                  );
                                  const totalMonto = rows.reduce(
                                    (s, a) => s + Number(a.valueHoy || 0),
                                    0
                                  );
                                  return (
                                    <>
                                      {rows.map((a, idx) => {
                                        const provId = pickProviderForAccount(
                                          a.accountName
                                        );
                                        const sharePct =
                                          totalsValue > 0
                                            ? (Number(a.valueHoy || 0) /
                                                totalsValue) *
                                              100
                                            : 0;
                                        return (
                                          <tr key={idx}>
                                            <td>{fmtProvider(provId)}</td>
                                            <td>{a.accountName}</td>
                                            <td className="num">
                                              {a.assetsCount ?? 0}
                                            </td>
                                            <td className="num">
                                              {formatCurrency(a.valueHoy)}
                                            </td>
                                            <td className="pct">
                                              {a.daily_return_pct == null
                                                ? "—"
                                                : percentText(
                                                    (a.daily_return_pct || 0) *
                                                      100
                                                  )}
                                            </td>
                                            <td className="num">
                                              {formatCurrency(a.delta_valueCLP)}
                                            </td>
                                            <td className="pct">
                                              {percentText(sharePct)}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                      <tr className="subtotal-row">
                                        <td>
                                          <strong>Total</strong>
                                        </td>
                                        <td></td>
                                        <td className="num">
                                          <strong>
                                            {totalInstruments.toLocaleString(
                                              "es-CL"
                                            )}
                                          </strong>
                                        </td>
                                        <td className="num">
                                          <strong>
                                            {formatCurrency(totalMonto)}
                                          </strong>
                                        </td>
                                        <td className="pct">
                                          <strong>
                                            {percentText(
                                              (totals.daily_return_pct || 0) *
                                                100
                                            )}
                                          </strong>
                                        </td>
                                        <td className="num">
                                          <strong>
                                            {formatCurrency(
                                              Number(totals.delta_valueCLP || 0)
                                            )}
                                          </strong>
                                        </td>
                                        <td className="pct">
                                          <strong>{percentText(100)}</strong>
                                        </td>
                                      </tr>
                                    </>
                                  );
                                })()}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="panel" style={{ marginTop: 12 }}>
                          <div className="panel-header">
                            Posiciones (Holdings)
                          </div>
                          <div className="table-wrap">
                            <table className="positions-table">
                              <thead>
                                <tr>
                                  <th>Cuenta</th>
                                  <th>Instrumento</th>
                                  <th>Nemo</th>
                                  <th>Clase</th>
                                  <th>Subclase</th>
                                  <th className="num">Títulos</th>
                                  <th className="num">Precio</th>
                                  <th className="num">Valor Hoy</th>
                                  <th className="num">Δ Día</th>
                                  <th className="pct">Retorno Diario</th>
                                  <th className="pct">% del Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {holdings.map((h, i) => {
                                  const sharePctHolding =
                                    Number(totals.valueHoy || 0) > 0
                                      ? (Number(h.valueHoy || 0) /
                                          Number(totals.valueHoy || 0)) *
                                        100
                                      : 0;
                                  return (
                                    <tr key={i}>
                                      <td>{h.accountName}</td>
                                      <td>{h.instrumentName}</td>
                                      <td>{h.instrumentCode ?? "—"}</td>
                                      <td>{h.assetClass ?? "—"}</td>
                                      <td>{h.subClass ?? "—"}</td>
                                      <td className="num">
                                        {h.quantityHoy == null
                                          ? "—"
                                          : Number(
                                              h.quantityHoy
                                            ).toLocaleString("es-CL")}
                                      </td>
                                      <td className="num">
                                        {h.priceHoy == null
                                          ? "—"
                                          : formatCurrency(h.priceHoy)}
                                      </td>
                                      <td className="num">
                                        {formatCurrency(h.valueHoy)}
                                      </td>
                                      <td className="num">
                                        {formatCurrency(h.delta_valueCLP)}
                                      </td>
                                      <td className="pct">
                                        {h.daily_return_pct == null
                                          ? "—"
                                          : percentText(
                                              (h.daily_return_pct || 0) * 100
                                            )}
                                      </td>
                                      <td className="pct">
                                        {percentText(sharePctHolding)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <div
                            style={{
                              marginTop: 8,
                              fontSize: 12,
                              color: "var(--muted-fg, #555)",
                            }}
                          >
                            Corte: {asof}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </>
      )}

      <PortfolioConfigModal
        isOpen={isConfigOpen}
        onClose={() => setConfigOpen(false)}
      />
    </div>
  );
}

export default PortfolioPage;
